import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { toast } from 'vue-sonner';
import dayjs from 'dayjs';
import {
    MY_ORDER_ACTIONS_KEY,
    ORDER_ACTIONS_KEY,
    SALES_ACTIONS_DIARY_KEY,
    SALES_ACTIONS_LIST_KEY,
    SALES_QUEUE_ACTIVE_KEY,
    type SalesAction,
} from '@composables/useSalesActions';

/**
 * The agent's working queue over a frozen set of sales actions.
 *
 * One query and five writes, all keyed on the same cache entry, because every
 * endpoint returns the *whole* queue: the controller treats a write as a
 * re-hydration, so the client never reconciles a partial response against its own
 * state and a queue that drifted — an item completed on the order screen, an
 * action deleted — corrects itself on the next interaction. Writes therefore
 * `setQueryData` the response rather than invalidating and refetching it.
 *
 * The queue is addressed by the authenticated user on read, so there is no key
 * per queue and nothing to invalidate when one queue displaces another.
 */
export const SALES_QUEUE_URL = '/sales/queues';

/**
 * Re-exported rather than declared: it is owned by `useSalesActions`, beside the
 * other keys a sales-action write invalidates. See the note there.
 */
export { SALES_QUEUE_ACTIVE_KEY };

/**
 * The footer's rendered height, published for the layout to reserve.
 *
 * Module-level rather than provided, because the two readers are not related:
 * the footer is mounted deep in `AuthenticatedLayout` and the offsets it forces
 * are on `main`'s height (so the last row of every table stays reachable) and on
 * `App.vue`'s teleported `Toaster` (so the feature's own feedback does not land
 * underneath the feature). Measured rather than hard-coded so the two can never
 * drift from what the footer actually occupies.
 */
export const salesQueueFooterHeight = ref(0);

export type SalesQueueStatus = 'ACTIVE' | 'COMPLETED' | 'CLOSED';

/** The four ways an agent leaves an item, as `SalesActionQueueOutcome` names them. */
export type SalesQueueOutcome = 'COMPLETED' | 'SKIPPED' | 'SNOOZED' | 'CANCELLED';

/**
 * Derived on the server from the actions themselves, never stored on the queue —
 * so an action completed anywhere else is counted here without the queue being
 * written to. There is deliberately no snoozed count: a snoozed item is an
 * outstanding item, and it lands in `pending`.
 */
export interface SalesQueueSummary {
    total: number;
    done: number;
    completed: number;
    cancelled: number;
    skipped: number;
    pending: number;
}

export interface SalesQueueItem {
    id: number;
    position: number;
    action: SalesAction | null;
    done: boolean;
    skipped: boolean;
    pending: boolean;
    /** False once the action is soft-deleted: the item stays, there is nothing to work. */
    available: boolean;
    visited_at: string | null;
}

export interface SalesQueue {
    id: number;
    status: SalesQueueStatus;
    user_id: number;
    started_at: string | null;
    ended_at: string | null;
    /**
     * Set aside, not put down. A paused queue is still `ACTIVE` and still carries
     * its cursor — only the footer stops rendering — so this is a presentation
     * flag rather than a fourth status.
     */
    paused: boolean;
    paused_at: string | null;
    items: SalesQueueItem[];
    current_item_id: number | null;
    current_position: number | null;
    /** The denominator, frozen at creation. Working the queue never moves it. */
    total: number;
    summary: SalesQueueSummary;
}

/** The filter set `StoreSalesActionQueueRequest` resolves, as the listing holds it. */
export interface SalesQueueFilters {
    user_id?: number[];
    action_type?: string[];
    status?: string[];
    date_from?: string | null;
    date_to?: string | null;
    search?: string | null;
}

interface QueueEnvelope {
    sales_action_queue: SalesQueue | null;
}

/**
 * The four surfaces that render the same actions from a different angle. Every
 * queue write can change an action's status or its scheduled moment, so all four
 * are invalidated on settle — the list behind the footer must not go on showing
 * an action as outstanding after the footer completed it.
 *
 * The order screen's Actions tab is in that set because the footer *drives the
 * page to it*: working an item leaves its own order on screen, so the tab is
 * almost always showing the very action just written.
 */
const DEPENDENT_KEYS = [SALES_ACTIONS_LIST_KEY, SALES_ACTIONS_DIARY_KEY, MY_ORDER_ACTIONS_KEY, ORDER_ACTIONS_KEY];

/**
 * The moment in the shape `UpdateOrderAction` stores it, which depends on how the
 * action is read: a timed action's `schedule_at` is a true UTC instant, an all-day
 * action's is a naive local date whose time component means nothing. Passing an
 * all-day value through a timezone conversion moves it onto the previous day for
 * any viewer behind UTC — the misleading-midnight defect the Sales formatters
 * exist to avoid, in its write direction.
 */
export function toScheduleAtPayload(value: string, allDay: boolean, timezone: string): string {
    return allDay ? dayjs(value).format('YYYY-MM-DD') : dayjs.tz(value, timezone).utc().format('YYYY-MM-DD HH:mm:ss');
}

/** The server's own message where it sent one, so a 409 or a 422 reads as itself. */
function messageFrom(error: unknown, fallback: string): string {
    const response = (error as { response?: { data?: { message?: string } } })?.response;

    return response?.data?.message ?? fallback;
}

export function useSalesQueue(enabled: Ref<boolean> | ComputedRef<boolean> | undefined = undefined) {
    const queryClient = useQueryClient();

    /**
     * Fails closed. `retry: false` because the footer is mounted on every
     * authenticated page: a queue that cannot be hydrated must become an absent
     * footer, quietly, rather than three retries and an error banner over the
     * whole CRM. The error is exposed so the footer can decline to render; it is
     * never toasted, since there is nothing the agent could do about it.
     */
    const query = useQuery({
        queryKey: SALES_QUEUE_ACTIVE_KEY,
        enabled: enabled ?? true,
        retry: false,
        queryFn: async (): Promise<SalesQueue | null> => {
            const { data } = await window.tenantAxios.get<QueueEnvelope>(`${SALES_QUEUE_URL}/active`);

            return data.sales_action_queue ?? null;
        },
    });

    const queue = computed<SalesQueue | null>(() => (query.isError.value ? null : (query.data.value ?? null)));

    const items = computed<SalesQueueItem[]>(() => queue.value?.items ?? []);

    const currentItem = computed<SalesQueueItem | null>(
        () => items.value.find((item) => item.id === queue.value?.current_item_id) ?? null,
    );

    const currentAction = computed<SalesAction | null>(() => currentItem.value?.action ?? null);

    /** An ended queue is not an active one, and the read endpoint never returns it. */
    const hasActiveQueue = computed(() => queue.value !== null && queue.value.status === 'ACTIVE');

    const isPaused = computed(() => hasActiveQueue.value && queue.value?.paused === true);

    /**
     * Whether the footer should be on screen. Distinct from `hasActiveQueue` on
     * purpose: a paused queue still exists, still holds the agent's place and
     * still displaces the next build — the Activity slide-out reads
     * `hasActiveQueue`, the footer reads this.
     */
    const isRunning = computed(() => hasActiveQueue.value && !isPaused.value);

    /**
     * "n of m" and the bar's fill. The numerator is the *cursor*, not the count of
     * finished items — an agent reads it as where they are, and the two diverge
     * the moment one item is completed out of order elsewhere. The summary carries
     * the finished counts separately, for the segments and the completion note.
     */
    const progress = computed(() => {
        const summary = queue.value?.summary;
        const total = queue.value?.total ?? 0;

        return {
            position: queue.value?.current_position ?? 0,
            total,
            done: summary?.done ?? 0,
            completed: summary?.completed ?? 0,
            cancelled: summary?.cancelled ?? 0,
            skipped: summary?.skipped ?? 0,
            pending: summary?.pending ?? 0,
            percent: total === 0 ? 0 : Math.round(((summary?.done ?? 0) / total) * 100),
        };
    });

    /** The item either side of the cursor, or null at the ends — what the steppers bind to. */
    const cursorIndex = computed(() => items.value.findIndex((item) => item.id === queue.value?.current_item_id));
    const previousItem = computed<SalesQueueItem | null>(() =>
        cursorIndex.value > 0 ? items.value[cursorIndex.value - 1] : null,
    );
    const nextItem = computed<SalesQueueItem | null>(() =>
        cursorIndex.value >= 0 && cursorIndex.value < items.value.length - 1 ?
            items.value[cursorIndex.value + 1]
        :   null,
    );

    function invalidateDependents(): void {
        DEPENDENT_KEYS.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
    }

    /**
     * Every endpoint answers with the whole queue, so the response *is* the new
     * cache entry. An ended queue is written as `null`: it is no longer the
     * active one, and the read endpoint would not return it again.
     */
    function absorb(next: SalesQueue | null): SalesQueue | null {
        const active = next !== null && next.status === 'ACTIVE' ? next : null;

        queryClient.setQueryData(SALES_QUEUE_ACTIVE_KEY, active);

        return next;
    }

    async function writeQueue(request: Promise<{ data: QueueEnvelope }>): Promise<SalesQueue | null> {
        const { data } = await request;

        return absorb(data.sales_action_queue ?? null);
    }

    /**
     * Building displaces whatever queue the agent already had — the server does
     * that unconditionally, and the CRM asks first, which is the only place that
     * question can usefully be put.
     */
    const createQueue = useMutation({
        mutationFn: ({ orderActionIds, filters }: { orderActionIds?: number[]; filters?: SalesQueueFilters }) =>
            writeQueue(
                window.tenantAxios.post<QueueEnvelope>(
                    SALES_QUEUE_URL,
                    orderActionIds ? { order_action_ids: orderActionIds } : { filters },
                ),
            ),
        onError: (error) => toast.error(messageFrom(error, 'Could not start the queue.')),
        onSettled: invalidateDependents,
    });

    /**
     * The hot path: one call, one transaction. A failed action write must not
     * leave the cursor advanced past a follow-up that was never made, which is
     * why the outcome and the cursor move together rather than as two requests.
     */
    const advance = useMutation({
        mutationFn: ({
            queueId,
            outcome,
            scheduleAt,
            allDay,
        }: {
            queueId: number;
            outcome: SalesQueueOutcome;
            scheduleAt?: string;
            /**
             * Sent only when the reschedule says so. An unsent flag leaves the
             * action in whichever mode it was already in, which is not the same
             * as sending `false` — the server treats the two differently on
             * purpose, because the flag decides how `schedule_at` is read.
             */
            allDay?: boolean;
        }) =>
            writeQueue(
                window.tenantAxios.post<QueueEnvelope>(`${SALES_QUEUE_URL}/${queueId}/advance`, {
                    outcome,
                    ...(scheduleAt === undefined ? {} : { schedule_at: scheduleAt }),
                    ...(allDay === undefined ? {} : { all_day: allDay }),
                }),
            ),
        onError: (error) => toast.error(messageFrom(error, 'Could not record that. Nothing has been changed.')),
        onSettled: invalidateDependents,
    });

    /**
     * The half of the queue's writes that touch no order action: stepping back,
     * stepping forward without working an item, and jumping by clicking a segment
     * of the progress bar. Split from `advance` along exactly that line, so
     * re-reading an item can never be mistaken for having worked it.
     */
    const moveCursor = useMutation({
        mutationFn: ({ queueId, itemId }: { queueId: number; itemId: number }) =>
            writeQueue(
                window.tenantAxios.patch<QueueEnvelope>(`${SALES_QUEUE_URL}/${queueId}`, { current_item_id: itemId }),
            ),
        onError: (error) => toast.error(messageFrom(error, 'Could not move to that item.')),
        onSettled: invalidateDependents,
    });

    /**
     * Setting the queue aside and picking it back up. Deliberately not modelled as
     * a client-side dismissal: a queue put down on a laptop should still be
     * waiting when the agent opens the CRM on another machine, and the one place
     * that can promise that is the row itself.
     */
    const setPaused = useMutation({
        mutationFn: ({ queueId, paused }: { queueId: number; paused: boolean }) =>
            writeQueue(window.tenantAxios.patch<QueueEnvelope>(`${SALES_QUEUE_URL}/${queueId}`, { paused })),
        onError: (error, { paused }) =>
            toast.error(messageFrom(error, paused ? 'Could not pause the queue.' : 'Could not resume the queue.')),
        onSettled: invalidateDependents,
    });

    /**
     * Re-ordering the line. Membership is frozen and the cursor is a pointer, so
     * this changes which follow-up is *next* and nothing else: no order action is
     * written, no outcome recorded, and the agent stays on the item they were on.
     *
     * The whole sequence goes every time. A partial list would let a client working
     * from a stale copy silently re-order around items it did not know existed, so
     * the server refuses anything that does not name every item exactly once — and
     * since membership is frozen, an up-to-date client always can.
     *
     * Deliberately *not* invalidating `DEPENDENT_KEYS` as the other writes do.
     * Those four surfaces render actions, and a re-sequence touches none — it moves
     * `position`, which only this queue has an opinion about. Absorbing the response
     * updates the one cache entry that changed.
     */
    const resequence = useMutation({
        mutationFn: ({ queueId, itemIds }: { queueId: number; itemIds: number[] }) =>
            writeQueue(
                window.tenantAxios.patch<QueueEnvelope>(`${SALES_QUEUE_URL}/${queueId}`, { item_order: itemIds }),
            ),
        onError: (error) => toast.error(messageFrom(error, 'Could not re-order the queue.')),
    });

    const closeQueue = useMutation({
        mutationFn: ({ queueId }: { queueId: number }) =>
            writeQueue(window.tenantAxios.patch<QueueEnvelope>(`${SALES_QUEUE_URL}/${queueId}`, { status: 'CLOSED' })),
        onError: (error) => toast.error(messageFrom(error, 'Could not close the queue.')),
        onSettled: invalidateDependents,
    });

    const isWriting = computed(
        () =>
            createQueue.isPending.value ||
            advance.isPending.value ||
            moveCursor.isPending.value ||
            resequence.isPending.value ||
            setPaused.isPending.value ||
            closeQueue.isPending.value,
    );

    return {
        query,
        queue,
        items,
        currentItem,
        currentAction,
        currentIndex: cursorIndex,
        previousItem,
        nextItem,
        hasActiveQueue,
        isPaused,
        isRunning,
        progress,
        isWriting,
        createQueue,
        advance,
        moveCursor,
        resequence,
        setPaused,
        closeQueue,
        invalidateDependents,
    };
}
